/**
 * RouteErrorBoundary — last-line-of-defense fallback. Catches render
 * crashes in ANY child route and shows a friendly "Something went wrong"
 * screen with a one-tap reload, instead of the previous blank-white
 * behavior. Logs the error to the console so debugging is still possible
 * in production.
 */
import React from "react";

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || "Unexpected error" };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[route-error-boundary]", err, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  handleHome = () => {
    try { window.location.href = "/welcome"; } catch (_) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-[100dvh] bg-[#F9F7F4] text-[#0A1128] flex flex-col items-center justify-center px-6 text-center"
        data-testid="route-error-boundary"
      >
        <img src="/assets/link-full-logo.svg?v=13" alt="Link" className="h-10 w-auto mb-7" />
        <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#ed6500] mb-2">Something went wrong</div>
        <h1 className="font-serif text-[28px] sm:text-[32px] leading-tight mb-3">Tap to reload</h1>
        <p className="text-[14px] text-[#515E7B] max-w-sm">
          We hit a snag rendering this page. Reload to try again, or head back to the welcome screen.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mt-6 w-full max-w-xs">
          <button
            type="button"
            onClick={this.handleReload}
            className="flex-1 h-12 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[12px] tracking-[0.22em] uppercase font-semibold transition-colors"
            data-testid="route-error-reload"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={this.handleHome}
            className="flex-1 h-12 rounded-2xl border border-[#0A1128]/15 hover:border-[#ed6500] text-[#0A1128] text-[12px] tracking-[0.22em] uppercase font-semibold transition-colors"
            data-testid="route-error-home"
          >
            Welcome
          </button>
        </div>
      </div>
    );
  }
}
